import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';

import {
  ClientReferenceNumberType,
  Loadable,
  ReferenceNumberType,
  ReferenceNumberTypeLevel,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import { ReferenceNumber } from '@/model';
import {
  LoadReferenceNumberTypesOptions,
  useReferenceNumberTypeStore,
} from '@/stores/ReferenceNumberType';

interface RefNumLoadable<T> extends Loadable<T> {
  data: T;
  error: unknown[];
}

export interface UseReferenceNumberTypesResponse {
  availableClientReferenceNumberTypes: ComputedRef<
    RefNumLoadable<ClientReferenceNumberType[]>
  >;
  availableReferenceNumberTypes: ComputedRef<
    RefNumLoadable<ReferenceNumberType[]>
  >;
  getDefaultReferenceNumbers: (
    clientReferenceNumberTypes?: ClientReferenceNumberType[],
  ) => ReferenceNumber[];
  lineItemReferenceNumberTypes: ComputedRef<
    Loadable<ClientReferenceNumberType[]>
  >;
  loadReferenceNumberTypes: (
    opts: LoadReferenceNumberTypesOptions,
  ) => Promise<void>;
  orderReferenceNumberTypes: ComputedRef<Loadable<ClientReferenceNumberType[]>>;
  shipmentReferenceNumberTypes: ComputedRef<
    Loadable<ClientReferenceNumberType[]>
  >;
}

export const useReferenceNumberTypes = (
  levels?: MaybeRefOrGetter<ReferenceNumberTypeLevel[]>,
): UseReferenceNumberTypesResponse => {
  const {
    shipmentReferenceNumberTypes,
    orderReferenceNumberTypes,
    lineItemReferenceNumberTypes,
    loadReferenceNumberTypes,
  } = useReferenceNumberTypeStore();

  const availableClientReferenceNumberTypes = computed(() => {
    const emptyLoadable: RefNumLoadable<ClientReferenceNumberType[]> = {
      data: [],
      loading: false,
      error: [],
    };

    return (
      toValue(levels)
        ?.map((level) => {
          switch (level) {
            case ReferenceNumberTypeLevel.SHIPMENT:
              return shipmentReferenceNumberTypes.value;
            case ReferenceNumberTypeLevel.ORDER:
              return orderReferenceNumberTypes.value;
            case ReferenceNumberTypeLevel.LINE_ITEM:
              return lineItemReferenceNumberTypes.value;
          }
        })
        .reduce((acc: RefNumLoadable<ClientReferenceNumberType[]>, curr) => {
          const data = acc.data
            .concat(curr?.data ?? [])
            .filter((refNumType, index, self) => {
              const foundIndex = self.findIndex(
                (r) =>
                  r.referenceNumberType?.naturalId ===
                  refNumType.referenceNumberType?.naturalId,
              );
              return foundIndex === index;
            });
          return {
            data,
            loading: acc.loading || (curr?.loading ?? false),
            error: acc.error.concat(curr?.error ?? []),
          };
        }, emptyLoadable) ?? emptyLoadable
    );
  });

  const availableReferenceNumberTypes = computed(() => {
    const data =
      availableClientReferenceNumberTypes.value.data
        ?.map((clientType) => clientType.referenceNumberType)
        ?.filter(notNull) ?? [];
    return {
      data,
      loading: availableClientReferenceNumberTypes.value.loading,
      error: availableClientReferenceNumberTypes.value.error,
    };
  });

  const getDefaultReferenceNumbers = (
    clientReferenceNumberTypes: ClientReferenceNumberType[] = [],
  ) => {
    return clientReferenceNumberTypes
      .filter((clientType) => clientType.displayByDefault)
      .map((clientType) => clientType.referenceNumberType)
      .filter(notNull)
      .map((type) => {
        return new ReferenceNumber({
          typeId: type.id,
          typeName: type.name,
          typeNaturalId: type.naturalId,
        });
      });
  };

  return {
    availableClientReferenceNumberTypes,
    availableReferenceNumberTypes,
    getDefaultReferenceNumbers,
    lineItemReferenceNumberTypes,
    loadReferenceNumberTypes,
    orderReferenceNumberTypes,
    shipmentReferenceNumberTypes,
  };
};
